import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { EntryStatusEnum, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import Grid from '@mui/material/Grid';
import { type Column, type Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import CompetitorsTableEmptyState from 'components/tables/competitors/CompetitorsTableEmptyState';
import CompetitorsTableSubtitle from 'components/tables/competitors/CompetitorsTableSubtitle';
import CompetitorsTableTitle from 'components/tables/competitors/CompetitorsTableTitle';
import { unpairedTableRequiredColumns } from 'components/tables/competitors/config';
import { useCompetitorsTableColumns } from 'components/tables/competitors/useCompetitorsTableColumns';
import DrawParticipantsTable from 'components/tables/eventsSplitDraws/drawParticipantsTable/DrawParticipantsTable';
import { BodyLargeLight } from 'components/typography/Typography';
import { Feature, featureEnabled } from 'config/clients';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import CreateDoubleTeamsContainer from 'containers/doubleTeams/CreateDoubleTeamsContainer';
import { useCompetitorsPopover } from 'containers/eventsSplitDraws/entries/EntriesActions';
import useUISettings from 'hooks/useUISettings';
import { type ParticipantInterface, type ParticipantPairArray } from 'typedefs/store/tmxTypes';

import UnpairedBulkActions from './UnpairedBulkActions';

const noMethods = {};

interface UnpairedParticipantsTableProps {
  event: any;
  pairedDrawParticipants: ParticipantPairArray;
  pairedIndividualParticipants: ParticipantInterface[];
  unpairedDrawParticipants: ParticipantInterface[];
  onSaveTeams: (participantIdPairs: Array<string[]>) => void;
}

const UnpairedParticipantsTable: React.FC<React.PropsWithChildren<UnpairedParticipantsTableProps>> = ({
  event,
  pairedDrawParticipants,
  pairedIndividualParticipants,
  unpairedDrawParticipants,
  onSaveTeams,
}) => {
  const { t } = useTranslation();
  const openCompetitorsPopover = useCompetitorsPopover();
  const showProfileDialog = usePlayersProfileDialog();
  const { tournamentDeskUI, updateSettings } = useUISettings() ?? {};
  const isDouble = event?.eventType === EventTypeEnum.DOUBLES;

  const handleOpenCompetitorsPopover = useCallback(
    (participant: ParticipantInterface, clickEvent: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
      openCompetitorsPopover(participant, noMethods, clickEvent.currentTarget, event),
    [openCompetitorsPopover, event],
  );

  const unpairedTableColumns = useCompetitorsTableColumns({
    classes: '',
    event,
    onClickAction: handleOpenCompetitorsPopover,
    onOpenPlayerProfile: showProfileDialog,
    requiredColumns: unpairedTableRequiredColumns,
  });
  const [isDoublesTeamsOverlayOpen, setIsDoublesTeamsOverlayOpen] = useState<boolean>(false);

  const handleOpenDoublesTeamsOverlay = () => {
    setIsDoublesTeamsOverlayOpen(true);
  };
  const handleCloseDoublesTeamsOverlay = () => {
    setIsDoublesTeamsOverlayOpen(false);
  };
  const isEmpty = unpairedDrawParticipants?.length === 0;

  const renderUnpairedBulkActions = useCallback(
    (selectedFlatRows: Row<ParticipantInterface>[], onResetRowSelection: () => void) => (
      <UnpairedBulkActions selectedFlatRows={selectedFlatRows} onResetRowSelection={onResetRowSelection} />
    ),
    [],
  );

  const additionalButtons = useMemo(
    () => (
      <>
        {!isEmpty && (
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item>
                <CreateDoublesTeamsButton onClick={handleOpenDoublesTeamsOverlay} />
              </Grid>
            </Grid>
          </Grid>
        )}
        {isEmpty && featureEnabled(Feature.MULTI_PAIRED_PARTICIPANTS) && (
          <CreateDoublesTeamsButton onClick={handleOpenDoublesTeamsOverlay} />
        )}
      </>
    ),
    [isEmpty],
  );

  const saveColumnsSelection = useCallback(
    (columns: Column<ParticipantInterface>[]) => {
      const selectedColumns = columns.map((column) => column.id);
      if (JSON.stringify(selectedColumns) === JSON.stringify(tournamentDeskUI?.unpairedColumns)) return;

      const update = {
        unpairedColumns: selectedColumns,
      };
      updateSettings(update);
    },
    [tournamentDeskUI?.unpairedColumns, updateSettings],
  );

  return (
    <>
      <Grid container direction="row" justifyContent="space-between">
        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <CompetitorsTableTitle length={`${unpairedDrawParticipants.length}`} text={t('unpairedPlayers')} />
              {!isEmpty && (
                <CompetitorsTableSubtitle subtitle={t('unpairedParticipantsTable.competitorsTableSubtitle')} />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!isEmpty ? (
        <DrawParticipantsTable
          isEnableMultiRowSelection
          additionalButtons={additionalButtons}
          columns={unpairedTableColumns}
          data={unpairedDrawParticipants}
          isDouble={isDouble}
          saveColumnsSelection={saveColumnsSelection}
          savedColumns={tournamentDeskUI?.unpairedColumns}
          selectedActions={renderUnpairedBulkActions}
          type={EntryStatusEnum.UNGROUPED}
        />
      ) : (
        <CompetitorsTableEmptyState>
          <BodyLargeLight>{t('noUnpaired')}</BodyLargeLight>
        </CompetitorsTableEmptyState>
      )}
      <CreateDoubleTeamsContainer
        closeDialog={handleCloseDoublesTeamsOverlay}
        isOpen={isDoublesTeamsOverlayOpen}
        pairedDrawParticipants={pairedDrawParticipants}
        pairedIndividualParticipants={pairedIndividualParticipants ?? []}
        saveTeams={onSaveTeams}
        unpairedDrawParticipants={unpairedDrawParticipants ?? []}
      />
    </>
  );
};

const CreateDoublesTeamsButton: React.FC<React.PropsWithChildren<{ onClick: () => void }>> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <StandardButton variant="primary" onClick={onClick}>
      {t('createDoublesTeams')}
    </StandardButton>
  );
};

export default UnpairedParticipantsTable;
